<script setup>
  /** @typedef {import('@/types').Id} Id */
  import {
    ref,
    computed,
    inject,
    provide,
    onMounted as mounted,
    watch,
    nextTick as tick
  } from 'vue'
  import AsDays from '@/components/as-days'
  import ThoughtAsArticle from '@/components/thoughts/as-article'
  import PosterAsFigure from '@/components/posters/as-figure'
  import AsSvgProcessing from '@/components/posters/as-svg-processing'
  import {
    as_created_at,
    load,
    as_type,
    as_author,
    as_layer_id,
    feed_slot_itemid
  } from '@/utils/itemid'
  import { del } from 'idb-keyval'
  import {
    as_directory_id,
    clear_author_dirs,
    load_directory_from_network
  } from '@/persistence/Directory'
  import { as_day_time_year } from '@/utils/date'
  import { Poster } from '@/persistence/Storage'
  import { current_user } from '@/utils/serverless'
  import { use as use_statements } from '@/use/statements'
  import { use as use_people } from '@/use/people'
  import { use_posters, geology_layers } from '@/use/poster'
  import { use_feed, authors_to_reload } from '@/use/feed'
  import { use_keymap } from '@/use/key-commands'
  import {
    storytelling,
    aspect_ratio_mode,
    menu,
    only_mine
  } from '@/utils/preference'
  import { posting, scroll_position } from '@/use/posting'
  import { axis_visibility } from '@/utils/intersection'
  import { after_layout } from '@/utils/after-layout'
  import { poster_delete_log } from '@/utils/poster-delete-log'
  import AsTextarea from '@/components/thoughts/as-textarea.vue'
  import { use_home_view } from '@/use/home-view'

  const on_toggle_keyboard = active => {
    posting.value = active ?? !posting.value
  }

  const set_working = inject('set_working')
  const select_photo = inject('select_photo')
  const init_processing_queue = inject('init_processing_queue')
  const queue_items = inject('queue_items')

  /** @type {import('vue').ComputedRef<import('@/persistence/Queue').QueueItem[]>} */
  const processing_items = computed(() => queue_items?.value ?? [])

  const working = ref(true)
  /** @type {import('vue').Ref<HTMLElement | null>} */
  const statements_ref = ref(null)
  /** @type {import('vue').Ref<import('@/types').Poster | null>} */
  const poster_to_remove = ref(null)
  /** @type {import('vue').Ref<HTMLDialogElement | null>} */
  const delete_dialog = ref(null)

  const { people, load_phonebook, phonebook } = use_people()
  const {
    for_person: statements_for_person,
    statements,
    statement_shown,
    update_statement
  } = use_statements()
  provide('update_statement', update_statement)
  const {
    for_person: posters_for_person,
    poster_shown,
    posters
  } = use_posters()

  const feed_needs_refresh = inject('feed_needs_refresh', null)
  /** @type {(reset?: boolean) => Promise<void>} */
  let fill_statements = async () => {}
  const shown_people_ids = () =>
    /** @type {Id[]} */ (people.value.map(person => person.id))

  const {
    load_feed_for_people,
    is_editable,
    overlay_for_day,
    overlay_statements_for_poster,
    overlay_editable_for_poster
  } = use_feed({
    posters,
    statements,
    statements_for_person,
    posters_for_person,
    refresh_signal: feed_needs_refresh,
    queue_items,
    on_refresh: async detail => {
      const reload_phonebook = detail?.reload_phonebook !== false
      const authors = Array.isArray(detail?.authors)
        ? authors_to_reload(detail, shown_people_ids())
        : null
      if (!reload_phonebook && !authors?.length) return
      if (set_working) set_working(true)
      try {
        if (reload_phonebook) {
          await load_phonebook()
          await fill_statements()
        } else await load_feed_for_people(/** @type {Id[]} */ (authors))
      } finally {
        if (set_working) set_working(false)
      }
    }
  })

  /**
   * @param {Id} id
   */
  const on_remove_poster = async id => {
    const loaded = await load(id)
    if (!loaded) return
    poster_to_remove.value = /** @type {import('@/types').Poster} */ (loaded)
    await tick()
    if (delete_dialog.value) delete_dialog.value.showModal()
  }

  /**
   * A thought merged into a poster is drawn as that poster's overlay, so its
   * `as-article` never mounts and never says it was seen. Left alone, the
   * oldest statements ride under the oldest posters and the statement history
   * behind them never opens. The poster speaks for the thought it carries.
   *
   * Takes the poster as an argument rather than returning a closure: a template
   * `@show="handler(day, item)"` compiles to an inline statement, so the value
   * it returns is thrown away and the work never happens.
   */
  const on_poster_show = (day, item, poster) => {
    poster_shown(poster)
    const overlay = overlay_statements_for_poster(day, item)
    if (overlay?.length) statement_shown(overlay)
  }

  const on_remove_missing_poster = async id => {
    poster_delete_log('Thoughts on_remove_missing_poster', { id })
    posters.value = posters.value.filter(item => id !== item.id)
    const author_id = as_author(/** @type {Id} */ (id))
    if (!author_id) {
      void del(as_directory_id(id))
      return
    }
    const keys_to_delete = [
      id,
      as_layer_id(/** @type {Id} */ (id), 'shadows'),
      ...geology_layers.map(layer => as_layer_id(/** @type {Id} */ (id), layer))
    ]
    poster_delete_log('Thoughts purging idb keys', { id, keys_to_delete })
    await Promise.all(keys_to_delete.map(key => del(key)))
    await clear_author_dirs(/** @type {Id} */ (author_id))
    const directory = await load_directory_from_network(
      /** @type {Id} */ (`${author_id}/posters`)
    )
    if (!directory?.items?.length) return
    const valid_timestamps = new Set(directory.items.map(Number))
    const oldest_valid = Math.min(...valid_timestamps)
    posters.value = posters.value.filter(item => {
      if (as_author(/** @type {Id} */ (item.id)) !== author_id) return true
      const created = Number(as_created_at(/** @type {Id} */ (item.id)))
      if (valid_timestamps.has(created)) return true
      if (created < oldest_valid) return true
      return false
    })
  }

  const on_confirmed_remove = async () => {
    const to_remove = poster_to_remove.value
    if (!to_remove || !delete_dialog.value) return
    delete_dialog.value.close()
    posters.value = posters.value.filter(item => to_remove.id !== item.id)
    const poster = new Poster(to_remove.id)
    await poster.delete()
  }

  const on_cancel_remove = () => {
    if (delete_dialog.value) delete_dialog.value.close()
  }

  const on_dialog_click = event => {
    if (event.target === delete_dialog.value && delete_dialog.value)
      delete_dialog.value.close()
  }

  /** Visibility dominates center distance when ranking posters in view. */
  const POSTER_VISIBILITY_WEIGHT = 1000

  /**
   * @param {number} visibility
   * @param {number} center
   * @param {number} mid
   */
  const poster_in_view_score = (visibility, center, mid) =>
    visibility * POSTER_VISIBILITY_WEIGHT - Math.abs(center - mid)

  const storytelling_scroll_container = () =>
    document.querySelector(
      'section[data-page][data-storytelling] section[data-days] > article'
    )

  /**
   * @param {Element | null | undefined} section
   * @param {ScrollBehavior} [behavior='smooth']
   */
  const scroll_storytelling_to_section = (section, behavior = 'smooth') => {
    const scroll_container = storytelling_scroll_container()
    if (!scroll_container || !section) return
    if (section.parentElement !== scroll_container) return
    const slide_rect = section.getBoundingClientRect()
    const container_rect = scroll_container.getBoundingClientRect()
    const scroll_left =
      scroll_container.scrollLeft +
      (slide_rect.left -
        container_rect.left -
        container_rect.width / 2 +
        slide_rect.width / 2)
    scroll_container.scrollTo({ left: scroll_left, behavior })
  }

  /** @param {HTMLElement} figure */
  const poster_itemid_from_figure = figure => {
    const id_el = figure.querySelector('[itemid]')
    return id_el?.getAttribute('itemid') || null
  }

  /** @param {HTMLElement} root */
  const poster_itemid_most_in_view = root => {
    let best_itemid = null
    let best_score = -Infinity
    const mid = window.innerHeight / 2
    for (const figure of root.querySelectorAll(
      'figure:has([itemtype$="/posters"])'
    )) {
      if (!(figure instanceof HTMLElement)) continue
      const rect = figure.getBoundingClientRect()
      if (rect.bottom < 0 || rect.top > window.innerHeight) continue
      const visible =
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
      const visibility = axis_visibility(
        visible,
        rect.height,
        window.innerHeight
      )
      const center = rect.top + rect.height / 2
      const score = poster_in_view_score(visibility, center, mid)
      if (score <= best_score) continue
      best_score = score
      best_itemid = poster_itemid_from_figure(figure)
    }
    return best_itemid
  }

  const storytelling_poster_itemid = () => {
    const root = statements_ref.value
    if (!root) return null
    const active = document.activeElement
    const focused =
      active instanceof HTMLElement
        ? active.closest('figure:has([itemtype$="/posters"])')
        : null
    if (focused instanceof HTMLElement && root.contains(focused)) {
      const itemid = poster_itemid_from_figure(focused)
      if (itemid) return itemid
    }
    const scroll_container = storytelling_scroll_container()
    if (!scroll_container) return null
    let best_itemid = null
    let best_score = -Infinity
    const container_rect = scroll_container.getBoundingClientRect()
    const mid = container_rect.left + container_rect.width / 2
    for (const section of scroll_container.querySelectorAll(
      ':scope > section'
    )) {
      const figure = section.querySelector('figure:has([itemtype$="/posters"])')
      if (!(figure instanceof HTMLElement)) continue
      const rect = section.getBoundingClientRect()
      if (rect.right < container_rect.left || rect.left > container_rect.right)
        continue
      const visible =
        Math.min(rect.right, container_rect.right) -
        Math.max(rect.left, container_rect.left)
      const visibility = axis_visibility(
        visible,
        rect.width,
        container_rect.width
      )
      const center = rect.left + rect.width / 2
      const score = poster_in_view_score(visibility, center, mid)
      if (score <= best_score) continue
      best_score = score
      best_itemid = poster_itemid_from_figure(figure)
    }
    return best_itemid
  }

  const focused_poster_itemid = () => {
    const root = statements_ref.value
    if (!root) return null
    const active = document.activeElement
    const focused =
      active instanceof HTMLElement
        ? active.closest('figure:has([itemtype$="/posters"])')
        : null
    if (focused instanceof HTMLElement && root.contains(focused)) {
      const itemid = poster_itemid_from_figure(focused)
      if (itemid) return itemid
    }
    return poster_itemid_most_in_view(root)
  }

  /**
   * @param {string} itemid
   * @param {ScrollBehavior} [behavior='smooth']
   */
  const scroll_feed_to_itemid = (itemid, behavior = 'smooth') => {
    const root = statements_ref.value
    if (!root || !itemid) return
    const escaped = CSS.escape(itemid)
    const figure = root
      .querySelector(`figure:has([itemtype$='/posters']) [itemid="${escaped}"]`)
      ?.closest('figure:has([itemtype$="/posters"])')
    if (!(figure instanceof HTMLElement)) return
    figure.scrollIntoView({ block: 'center', behavior })
    figure.focus()
  }

  /**
   * @param {string} itemid
   * @param {ScrollBehavior} [behavior='smooth']
   */
  const scroll_storytelling_to_itemid = (itemid, behavior = 'smooth') => {
    if (!itemid) return
    const escaped = CSS.escape(itemid)
    const figure = document.querySelector(
      `section[data-page][data-storytelling] figure:has([itemtype$='/posters']) [itemid="${escaped}"]`
    )
    const section = figure?.closest('section')
    scroll_storytelling_to_section(section, behavior)
  }

  /** @param {Event} e */
  const focus_first_poster = e => {
    const first = statements_ref.value?.querySelector(
      'figure:has([itemtype$="/posters"])'
    )
    if (!(first instanceof HTMLElement)) return
    e.preventDefault()
    first.focus()
  }

  const on_focus = event => {
    if (!storytelling.value) return
    scroll_storytelling_to_section(event.target.closest('section'))
  }

  watch(
    storytelling,
    async (is_on, was_on) => {
      if (is_on && !was_on) {
        const itemid = focused_poster_itemid()
        if (!itemid) return
        await tick()
        await after_layout()
        scroll_storytelling_to_itemid(itemid, 'auto')
        return
      }
      if (!is_on && was_on) {
        const itemid = storytelling_poster_itemid()
        if (!itemid) return
        await tick()
        await after_layout()
        scroll_feed_to_itemid(itemid, 'auto')
      }
    },
    { flush: 'pre' }
  )

  fill_statements = async (reset = false) => {
    const my_id =
      (typeof window !== 'undefined' ? window.localStorage?.me : null) ?? null

    if (only_mine.value)
      people.value = my_id ? [{ id: my_id, type: 'person' }] : []
    else {
      if (phonebook.value.length)
        people.value = /** @type {import('@/types').Item[]} */ ([
          ...phonebook.value
        ])
      if (my_id && !people.value.some(p => p.id === my_id))
        people.value.push({ id: my_id, type: 'person' })
      const admin_raw = import.meta.env.VITE_ADMIN_ID
      const admin_itemid = admin_raw
        ? /** @type {Id} */ (`/${String(admin_raw).replace(/^\/?/, '')}`)
        : null
      if (
        !current_user.value &&
        admin_itemid &&
        !people.value.some(p => p.id === admin_itemid)
      )
        people.value.push({
          id: admin_itemid,
          type: 'person'
        })
    }

    await load_feed_for_people(
      /** @type {Id[]} */ (people.value.map(relation => relation.id)),
      { reset }
    )
  }

  const mark_thoughts_rendered = async () => {
    if (typeof window === 'undefined') return
    const perf = window.performance
    if (!perf?.mark) return

    await tick()
    await after_layout()

    perf.clearMarks('thoughts-rendered')
    perf.mark('thoughts-rendered')
  }

  const remove_thoughts_shell = () => {
    document.querySelector('.thoughts-shell')?.remove()
  }

  const { view: home_view, shows } = use_home_view()

  const { register } = use_keymap('Thoughts')
  register('poster::Create_New', () => select_photo?.())

  /**
   * Runs the full feed load once, waiting for auth to resolve first. Auth is
   * deferred to after first paint (init_serverless runs in a rAF), so `mounted`
   * can beat it; loading without the phonebook would paint just me, then the
   * auth watcher would load everyone and the feed would glitch and re-render.
   * Holding `working` through the whole load keeps it to one paint.
   * @returns {Promise<void>}
   */
  let initial_feed = null
  const load_initial_feed = () => {
    if (initial_feed) return initial_feed
    initial_feed = new Promise(resolve => {
      const start = async () => {
        working.value = true
        try {
          await Promise.all([fill_statements(), load_phonebook()])
          await fill_statements()
          await init_processing_queue?.()
          await mark_thoughts_rendered()
        } finally {
          working.value = false
          resolve()
        }
      }
      if (current_user.value !== undefined) void start()
      else {
        const stop = watch(
          () => current_user.value,
          user => {
            if (user === undefined) return
            stop()
            void start()
          }
        )
      }
    })
    return initial_feed
  }

  watch(
    () => current_user.value,
    async (user, was) => {
      if (was === undefined || !user) return
      await load_phonebook()
      await fill_statements()
    }
  )

  watch(only_mine, async () => {
    working.value = true
    try {
      await fill_statements(true)
    } finally {
      working.value = false
    }
  })

  mounted(async () => {
    await tick()
    await after_layout()
    remove_thoughts_shell()
    await load_initial_feed()
  })

  watch(posting, async (now, was) => {
    if (was && !now && scroll_position.value !== null) {
      await tick()
      window.scrollTo(0, scroll_position.value)
      scroll_position.value = null
    }
  })
</script>

<template>
  <dialog
    v-if="poster_to_remove"
    ref="delete_dialog"
    data-confirm
    @click="on_dialog_click">
    <article>
      <header>
        <h1>Delete Poster</h1>
      </header>
      <p>
        <i>Created:</i>
        {{ as_day_time_year(as_created_at(poster_to_remove.id)) }}
      </p>
      <menu>
        <button @click="on_cancel_remove">Cancel</button>
        <button data-delete @click="on_confirmed_remove">Delete</button>
      </menu>
      <footer></footer>
    </article>
  </dialog>
  <section
    id="thoughts"
    ref="statements_ref"
    data-page
    :data-storytelling="storytelling || undefined"
    :data-slice="aspect_ratio_mode !== 'auto' || undefined"
    :data-chat="home_view === 'chat-thoughts' || undefined"
    :inert="!shows.thoughts || undefined">
    <h1>Thoughts</h1>
    <as-textarea
      @toggle-keyboard="on_toggle_keyboard"
      @tab-next="focus_first_poster" />
    <section v-if="processing_items.length" data-processing-queue>
      <as-svg-processing
        v-for="item in processing_items"
        :key="item.id"
        :queue_item="item" />
    </section>

    <as-days
      v-slot="{ day }"
      :working="working"
      :posters="posters"
      :statements="statements"
      :storytelling="storytelling"
      itemscope
      @focusin="on_focus">
      <template v-for="item in day" :key="feed_slot_itemid(item)">
        <poster-as-figure
          v-if="
            (item.type === 'posters' ||
              as_type(feed_slot_itemid(item)) === 'posters') &&
            !posting
          "
          :itemid="feed_slot_itemid(item)"
          :menu="menu"
          :slice="aspect_ratio_mode !== 'auto'"
          :overlay_statements="overlay_statements_for_poster(day, item)"
          :overlay_editable="overlay_editable_for_poster(day, item)"
          tabindex="0"
          @show="poster => on_poster_show(day, item, poster)"
          @remove="on_remove_poster"
          @missing="on_remove_missing_poster" />
        <thought-as-article
          v-else-if="
            item.type !== 'posters' &&
            !overlay_for_day(day).merged_thought_keys.has(
              feed_slot_itemid(item)
            )
          "
          :statements="item"
          :editable="is_editable(item)"
          verbose
          @show="statement_shown" />
      </template>
    </as-days>
  </section>
</template>

<style lang="stylus">
  :root:has(section#thoughts[inert]) {
    overflow: hidden;
    & footer[data-footer-visible] {
      visibility: hidden;
    }
  }
  section#thoughts {
    position: relative;
    transition: padding-inline-start var(--duration-subject, 0.3s) var(--ease-reveal, ease-out);
    @media (min-width: display-begins) {
      &[data-chat] {
        padding-inline-start: page-width;
      }
    }
    display: flex;
    flex-direction: column;
    & > section[data-processing-queue] {
      standard-grid: gentle;
      grid-auto-flow: row;
      grid-gap: base-line;
      padding: 0 base-line;
      margin-bottom: base-line;
      & > figure[itemtype$='/posters'][aria-busy] {
        grid-column: 1 / -1;
      }
    }
    &[data-slice] > section[data-processing-queue] {
      grid-gap: 0;
    }
    & > section[data-days] {
      padding-left: 0;
      padding-right: 0;
      &[role='feed'] > article > header {
        padding-left: base-line;
        padding-right: base-line;
      }
      & article[data-thought] {
        padding-left: base-line;
        padding-right: base-line;
      }
      &[role='feed'] > article figure:has([itemtype$='/posters']) {
        border-radius: 0;
      }
      &[role='feed'] > article {
        @media (prefers-color-scheme: dark) {
          & > header h4, figure:has([itemtype$='/posters']) > svg[data-icon='background'] {
            color: var(--accent);
          }
        }
      }
      & h4 {
        margin: base-line 0 0 0;
      }
      &[role='feed'] > article p[itemprop='statement']:focus {
        font-weight: bolder;
        outline: 0;
      }
    }
    &[data-storytelling] {
      flex: 1;
      min-height: 100vh;
      & > h1,
      & > section[data-processing-queue],
      & > fieldset:has(> textarea#wat) {
        display: none;
      }
    }
    svg[data-icon='working'] {
     fill: var(--accent);
    }
  }
</style>
